<template>
  <div class="gift-summary-container">
    <div class="gift-content">
      <div class="gift-header">
        <!-- タイトル -->
        <div class="gift-title">{{ $t('historiesTopReceiveGift') }}</div>
      </div>

      <!-- ギフトリスト -->
      <div class="gift-list">
        <div 
          v-for="(gift, index) in giftList" 
          :key="index" 
          class="gift-item"
        >
          <div class="gift-item-icon">
            <img 
              :src="gift.icon" 
              :alt="gift.name" 
              class="gift-icon-img"
            />
          </div>
          <div class="gift-item-info">
            <div class="gift-count">{{ $t('commonGiftAmount', { count: gift.count }) }}</div>
            <div class="gift-name">{{ gift.name }}</div>
          </div>
        </div>
      </div>

      <!-- 区切り線 -->
      <div class="divider-line">
        <svg class="divider-svg" viewBox="0 0 311 2" fill="none">
          <path
            d="M0 1H311"
            stroke="white"
            stroke-width="1"
            opacity="0.5"
          />
        </svg>
      </div>

      <!-- 詳細ギフト -->
      <div class="detailed-gifts">
        <div 
          v-for="(gift, index) in detailedGifts"
          :key="index"
          class="detailed-gift-wrapper"
        >
          <DetailedGift :gift="gift" />
          <!-- 区切り線（最後以外） -->
          <div 
            v-if="index < detailedGifts.length - 1"
            class="divider-line"
          >
            <svg class="divider-svg" viewBox="0 0 311 2" fill="none">
              <path
                d="M0 1H311"
                stroke="white"
                stroke-width="1"
                opacity="0.5"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import DetailedGift from './DetailedGift.vue'
import { HistoryAlbumKeyedData } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumKeyedData'
import { HistoryAlbumUser } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumUser'
import { HistoryAlbumReceiveGiftRecord } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumReceiveGiftRecord'

@Component({
  components: {
    DetailedGift
  }
})
export default class GiftSummary extends Vue {
  @Prop({ type: Number, required: true })
  year!: number

  // 年別データをpropsで受け取る
  @Prop({ type: Object, default: null })
  yearData!: HistoryAlbumKeyedData | null

  // ユーザー情報をpropsで受け取る
  @Prop({ type: Object, default: null })
  user!: HistoryAlbumUser | null

  // 簡易ギフトリスト（yeardataの構造体をそのまま使用）
  get giftList() {
    if (!this.yearData || !this.yearData.receiveGiftRecords) {
      return []
    }

    return this.yearData.receiveGiftRecords.map(record => ({
      icon: record.gift?.iconUrl || '',
      count: record.totalCount.toLocaleString(),
      name: record.gift?.name || ''
    }))
  }

  // 詳細ギフト情報（yeardataの構造体をそのまま使用）
  get detailedGifts(): HistoryAlbumReceiveGiftRecord[] {
    if (!this.yearData || !this.yearData.receiveGiftRecords) {
      return []
    }

    // 受け取ったギフトレコードをそのまま返す
    return this.yearData.receiveGiftRecords
  }
}
</script>

<style scoped>
.gift-summary-container {
  position: relative;
  width: 100%;
  border-radius: 8px;
  background: linear-gradient(126deg, #FF8E5A 0%, #FFD447 100%);
  overflow: hidden;
}

/* 背景のGIFTテキスト */
.gift-summary-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 220px;
  height: 50px;
  background-image: url('/img/campaign/reality_histories_20m/img_text_gift_orange.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: left top;
  opacity: 1;
  z-index: 1;
}

/* 背景のギフトボックス */
.gift-summary-container::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 120px;
  height: 120px;
  background-image: url('/img/campaign/reality_histories_20m/img_gift_orange.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: right top;
  opacity: 1;
  z-index: 1;
}

.gift-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 32px 16px 16px 16px;
  position: relative;
  width: 100%;
  z-index: 2;
}

/* ヘッダー */
.gift-header {
  position: relative;
  width: 100%;
}

.gift-title {
  font-weight: 600;
  font-style: normal;
  font-size: 26px;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
  line-height: normal;
  position: relative;
  z-index: 3;
}

/* ギフトリスト */
.gift-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  position: relative;
  width: 100%;
}

.gift-item {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  position: relative;
  width: 100%;
}

.gift-item-icon {
  background: #ffffff;
  border-radius: 4px;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gift-icon-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gift-item-info {
  flex: 1;
  min-height: 1px;
  min-width: 1px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  line-height: 0;
  font-style: normal;
  padding: 0;
  color: #ffffff;
  text-align: left;
}

.gift-count {
  font-weight: 300;
  position: relative;
  flex-shrink: 0;
  font-size: 11px;
  width: 100%;
  line-height: normal;
}

.gift-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  flex-shrink: 0;
  font-size: 16px;
  white-space: nowrap;
  width: 100%;
  line-height: normal;
}

/* 区切り線 */
.divider-line {
  position: relative;
  flex-shrink: 0;
  width: 100%;
}

.divider-svg {
  display: block;
  width: 100%;
  height: 100%;
}

/* 詳細ギフト */
.detailed-gifts {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.detailed-gift-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}
</style>
